import { useCallback, useEffect, useState } from 'react';
import { useReduxState } from 'hooks';
import { getDefaultTextHighlightColors } from 'lib/colors';
import { DropdownSelectable } from 'components/common/dropdown';

import type { BlockProps } from 'components/editor/blocks/EditorBlock';

type Props = Pick<BlockProps, 'data' | 'updateData'>;

const EditorBlockTextHighlightControls = ({ data, updateData }: Props) => {
  const { messages, theme } = useReduxState();

  const options =
    theme.textHighlightColors || getDefaultTextHighlightColors(theme);

  const [selectedOptionIndex, setSelectedOptionIndex] = useState(() => {
    const index =
      options.findIndex(
        (option) =>
          option.color === data.color &&
          (option.backgroundColor === data.backgroundColor ||
            option.borderColor === data.borderColor)
      ) + 1;
    return index;
  });

  useEffect(() => {
    const index =
      options.findIndex(
        (option) =>
          option.color === data.color &&
          (option.backgroundColor === data.backgroundColor ||
            option.borderColor === data.borderColor)
      ) + 1;
    if (selectedOptionIndex !== index) setSelectedOptionIndex(index);
  }, [data]);

  const handleUpdate = useCallback(
    (value) => {
      setSelectedOptionIndex(value);
      data.backgroundColor = options[value - 1]?.backgroundColor || null;
      data.borderColor = options[value - 1]?.borderColor || null;
      data.color = options[value - 1]?.color || null;
      if (value === 0) data.textSize = null;
      updateData(data);
    },
    [data]
  );

  const handleSizeUpdate = useCallback(
    (value) => {
      data.textSize = value === 'larger' ? 'larger' : null;
      updateData(data);
    },
    [data]
  );

  return (
    <div className="wrapper">
      <DropdownSelectable
        align="left"
        category="lighter"
        defaultText={messages.commonHighlight}
        defaultValue={selectedOptionIndex}
        onChange={handleUpdate}
        options={[
          {
            text: messages.commonNoHighlight,
            value: 0
          },
          ...options.map((option, index) => ({
            text: (
              <div
                style={{
                  backgroundColor: option.backgroundColor || 'transparent',
                  border: option.borderColor
                    ? `2px solid ${option.borderColor}`
                    : 'none',
                  borderRadius: 5,
                  color: option.color,
                  display: 'inline-block',
                  padding: option.borderColor ? '0px 5px' : '2px 7px'
                }}
              >
                {messages.commonText}
              </div>
            ),
            value: index + 1
          }))
        ]}
        size="small"
        style={{ marginRight: 10 }}
        value={selectedOptionIndex}
      />

      {(data.backgroundColor || data.borderColor) && data.color ? (
        <DropdownSelectable
          align="left"
          category="lighter"
          defaultValue={data.textSize === 'larger' ? 'larger' : 'normal'}
          onChange={handleSizeUpdate}
          options={[
            {
              text: messages.commonNormal,
              value: 'normal'
            },
            {
              text: messages.commonLarger,
              value: 'larger'
            }
          ]}
          selectedValueBefore={`${messages.commonText}:`}
          showSelectedText={true}
          size="small"
          style={{ marginRight: 10 }}
          value={data.textSize}
        />
      ) : null}
    </div>
  );
};

export default EditorBlockTextHighlightControls;
